import cn from '@integrations/classnames'
import {
  type HTMLAttributes,
  type PointerEvent as ReactPointerEvent,
  type UIEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from '@integrations/react'
import styles from './PageShell.module.scss'

const MIN_THUMB_SIZE = 32
const IDLE_MS = 1000 // how long the thumb stays after the last scroll, like the default overlay scrollbar

type Axis = 'x' | 'y'

interface Thumb {
  visible: boolean
  /** Distance from the start of the track, in px. */
  offset: number
  /** Length along the track, in px. */
  size: number
}

interface DragStart {
  pointer: number
  scroll: number
}

const HIDDEN: Thumb = { visible: false, offset: 0, size: 0 }

/** What an axis reads from the scrolling part: its window, its content, where it is scrolled and the track length. */
function readAxis(axis: Axis, scroller: HTMLElement, track: HTMLElement) {
  return axis === 'y'
    ? {
        viewport: scroller.clientHeight,
        content: scroller.scrollHeight,
        position: scroller.scrollTop,
        trackSize: track.clientHeight
      }
    : {
        viewport: scroller.clientWidth,
        content: scroller.scrollWidth,
        position: scroller.scrollLeft,
        trackSize: track.clientWidth
      }
}

function computeThumb(axis: Axis, scroller: HTMLElement, track: HTMLElement): Thumb {
  const { viewport, content, position, trackSize } = readAxis(axis, scroller, track)
  const overflow = content - viewport
  if (trackSize <= 0 || overflow <= 1) return HIDDEN
  const size = Math.min(trackSize, Math.max(MIN_THUMB_SIZE, (viewport / content) * trackSize))
  return { visible: true, offset: (position / overflow) * (trackSize - size), size }
}

function sameThumb(a: Thumb, b: Thumb) {
  return a.visible === b.visible && a.offset === b.offset && a.size === b.size
}

/**
 * The scrolling part of a `fill` page's list card. From the desktop width the native scrollbars are hidden and slim
 * ones are drawn: a vertical one under the table header and a horizontal one along the bottom edge, both inset from
 * the card edge (CSS cannot place a native scrollbar there, and only WebKit can restyle it), so they look the same in
 * every browser. The horizontal one is what shows that a wide table (many columns on) scrolls sideways. Wheel, touch
 * and keyboard scrolling are the browser's own; a thumb follows the scroll position and can be dragged, and a click
 * on its track pages. Like the default overlay scrollbar they are hidden at rest, show while the list scrolls (and a
 * second after) and while the pointer is over the list, and widen when pointed at. A track takes the pointer only
 * while it has a thumb (`page-fill-track--active`): its invisible strip would otherwise cover the rows under it.
 */
export function PageFillScroll({ className, children, onScroll, ...props }: HTMLAttributes<HTMLDivElement>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const trackYRef = useRef<HTMLDivElement>(null)
  const trackXRef = useRef<HTMLDivElement>(null)
  const drag = useRef<Record<Axis, DragStart | null>>({ y: null, x: null })
  const idleTimer = useRef<number | undefined>(undefined)
  const [thumbs, setThumbs] = useState<Record<Axis, Thumb>>({ y: HIDDEN, x: HIDDEN })
  const [awake, setAwake] = useState(false) // scrolled or dragged a moment ago

  function trackOf(axis: Axis) {
    return axis === 'y' ? trackYRef.current : trackXRef.current
  }

  const measure = useCallback(() => {
    const scroller = scrollRef.current
    const trackY = trackYRef.current
    const trackX = trackXRef.current
    if (!scroller || !trackY || !trackX) return
    const next = { y: computeThumb('y', scroller, trackY), x: computeThumb('x', scroller, trackX) }
    setThumbs((current) => (sameThumb(current.y, next.y) && sameThumb(current.x, next.x) ? current : next))
  }, [])

  // Show the thumbs while the list moves, hide them a moment after it stops (hovering shows them too, in CSS)
  const wake = useCallback(() => {
    setAwake(true)
    window.clearTimeout(idleTimer.current)
    idleTimer.current = window.setTimeout(() => setAwake(false), IDLE_MS)
  }, [])

  useEffect(() => () => window.clearTimeout(idleTimer.current), [])

  // a drag cannot outlive its thumb: when the list gets short the thumb goes away and no pointerup arrives
  useEffect(() => {
    if (!thumbs.y.visible) drag.current.y = null
    if (!thumbs.x.visible) drag.current.x = null
  }, [thumbs.y.visible, thumbs.x.visible])

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    measure()
    wake()
    onScroll?.(event) // the caller's own handler runs as well
  }

  // the list changes with every render of the page (rows, pagination), so measure after each one
  useLayoutEffect(measure)

  // ...and when the card is resized or the content grows by itself (window resize, details opening)
  useEffect(() => {
    const scroller = scrollRef.current
    if (!scroller || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(scroller)
    for (const child of Array.from(scroller.children)) observer.observe(child)
    return () => observer.disconnect()
  })

  function scrollTo(axis: Axis, value: number) {
    const scroller = scrollRef.current
    if (!scroller) return
    if (axis === 'y') scroller.scrollTop = value
    else scroller.scrollLeft = value
  }

  function pointerOf(axis: Axis, event: ReactPointerEvent<HTMLDivElement>) {
    return axis === 'y' ? event.clientY : event.clientX
  }

  function handleThumbPointerDown(axis: Axis, event: ReactPointerEvent<HTMLDivElement>) {
    const scroller = scrollRef.current
    if (event.button !== 0 || !scroller) return // only the primary button drags (not a right click)
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    drag.current[axis] = {
      pointer: pointerOf(axis, event),
      scroll: axis === 'y' ? scroller.scrollTop : scroller.scrollLeft
    }
    window.clearTimeout(idleTimer.current)
    setAwake(true)
  }

  function handleThumbPointerMove(axis: Axis, event: ReactPointerEvent<HTMLDivElement>) {
    const scroller = scrollRef.current
    const track = trackOf(axis)
    const start = drag.current[axis]
    if (!start || !scroller || !track) return
    if (event.buttons === 0) {
      drag.current[axis] = null // the button was released somewhere we did not hear about
      return
    }
    const { viewport, content, trackSize } = readAxis(axis, scroller, track)
    const travel = trackSize - thumbs[axis].size
    if (travel <= 0) return
    scrollTo(axis, start.scroll + ((pointerOf(axis, event) - start.pointer) * (content - viewport)) / travel)
  }

  function handleThumbPointerUp(axis: Axis, event: ReactPointerEvent<HTMLDivElement>) {
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    drag.current[axis] = null
    wake()
  }

  function handleTrackPointerDown(axis: Axis, event: ReactPointerEvent<HTMLDivElement>) {
    const scroller = scrollRef.current
    const track = trackOf(axis)
    if (!scroller || !track || !thumbs[axis].visible || event.target !== event.currentTarget) return
    const rect = track.getBoundingClientRect()
    const before =
      axis === 'y' ? event.clientY < rect.top + thumbs.y.offset : event.clientX < rect.left + thumbs.x.offset
    const { viewport, position } = readAxis(axis, scroller, track)
    scrollTo(axis, position + (before ? -1 : 1) * viewport * 0.9)
  }

  return (
    <div className={cn(styles['page-fill-frame'], 'page-fill-frame')}>
      <div
        ref={scrollRef}
        className={cn(styles['page-fill-scroll'], 'page-fill-scroll', className)}
        onScroll={handleScroll}
        {...props}
      >
        {children}
      </div>
      <div
        ref={trackYRef}
        className={cn(styles['page-fill-track'], 'page-fill-track', {
          [styles['page-fill-track--active']]: thumbs.y.visible,
          'page-fill-track--active': thumbs.y.visible,
          [styles['page-fill-track--awake']]: awake,
          'page-fill-track--awake': awake
        })}
        onPointerDown={(event) => handleTrackPointerDown('y', event)}
        aria-hidden='true'
        data-test='page-fill-track'
      >
        {thumbs.y.visible && (
          // the thumb's size and place are computed from the scroll position, so they are inline
          <div
            className={cn(styles['page-fill-thumb'], 'page-fill-thumb')}
            style={{ height: thumbs.y.size, transform: `translateY(${thumbs.y.offset}px)` }}
            onPointerDown={(event) => handleThumbPointerDown('y', event)}
            onPointerMove={(event) => handleThumbPointerMove('y', event)}
            onPointerUp={(event) => handleThumbPointerUp('y', event)}
            onPointerCancel={(event) => handleThumbPointerUp('y', event)}
            data-test='page-fill-thumb'
          />
        )}
      </div>
      <div
        ref={trackXRef}
        className={cn(styles['page-fill-track-x'], 'page-fill-track-x', {
          [styles['page-fill-track--active']]: thumbs.x.visible,
          'page-fill-track--active': thumbs.x.visible,
          [styles['page-fill-track--awake']]: awake,
          'page-fill-track--awake': awake
        })}
        onPointerDown={(event) => handleTrackPointerDown('x', event)}
        aria-hidden='true'
        data-test='page-fill-track-x'
      >
        {thumbs.x.visible && (
          <div
            className={cn(styles['page-fill-thumb-x'], 'page-fill-thumb-x')}
            style={{ width: thumbs.x.size, transform: `translateX(${thumbs.x.offset}px)` }}
            onPointerDown={(event) => handleThumbPointerDown('x', event)}
            onPointerMove={(event) => handleThumbPointerMove('x', event)}
            onPointerUp={(event) => handleThumbPointerUp('x', event)}
            onPointerCancel={(event) => handleThumbPointerUp('x', event)}
            data-test='page-fill-thumb-x'
          />
        )}
      </div>
    </div>
  )
}
